import React, { useState, useEffect, useRef } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, ShoppingCart, Package, UtensilsCrossed,
  ClipboardList, BarChart3, Settings,
  LogOut, Menu, X, ChevronDown,
  Wallet, Calculator, Activity, Bot, UserSquare2, BarChart2, Layers3
} from 'lucide-react';
import useAuthStore from '../store/authStore';
import OfflineIndicator from './OfflineIndicator';
import api from '../api/axios';
import { applyTheme } from '../utils/theme';

// Reads the deployed JS bundle hash from the loaded <script> tag — changes every build,
// so users can confirm at a glance whether they're on the latest code (vs a cached one).
const getBuildId = () => {
  try {
    const script = Array.from(document.querySelectorAll('script[src]'))
      .map((s) => s.getAttribute('src'))
      .find((src) => /\/static\/js\/main\.[a-z0-9]+\.js/.test(src || ''));
    const match = script && script.match(/main\.([a-z0-9]+)\.js/);
    return match ? match[1] : 'dev';
  } catch {
    return 'dev';
  }
};

const Layout = () => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [settings, setSettings] = useState(null);
  const { user, logout, hasPermission, isAdmin, isStaff, refreshPermissions } = useAuthStore();
  const hasStaffProfile = user?.has_staff_profile === true;
  const navigate = useNavigate();
  const location = useLocation();
  const lastRefresh = useRef(0);

  // Refresh permissions on route change (max once every 30 seconds)
  useEffect(() => {
    const now = Date.now();
    if (now - lastRefresh.current > 30000) {
      lastRefresh.current = now;
      refreshPermissions();
    }
  }, [location.pathname, refreshPermissions]);

  useEffect(() => {
    api.get('/settings/').then((res) => {
      setSettings(res.data);
      if (res.data?.theme_primary) applyTheme(res.data.theme_primary);
    }).catch(() => {});
  }, []);

  // Close mobile sidebar when clicking outside
  const closeMobileSidebar = () => setMobileSidebarOpen(false);

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const shopLogo = settings?.shop_logo || '/logo1.png';
  const shopName = settings?.shop_name || 'Shop';

  // Reflect the configured business name in the browser tab (no hardcoded brand).
  useEffect(() => {
    if (settings?.shop_name) document.title = `${settings.shop_name} · POS`;
  }, [settings?.shop_name]);

  const menuItems = isStaff() ? [
    { path: '/staff-portal', icon: LayoutDashboard, label: 'Dashboard', show: true },
    { path: '/staff', icon: UserSquare2, label: hasPermission('staff', 'view') || hasPermission('staff', 'add') ? 'Staff' : 'Attendance', show: hasPermission('attendance', 'view') || hasPermission('attendance', 'add') || hasPermission('leaves', 'view') || hasPermission('leaves', 'add') },
  ] : [
    { path: '/staff-portal', icon: LayoutDashboard, label: 'Dashboard', show: hasStaffProfile },
    { path: '/pos', icon: ShoppingCart, label: 'POS', show: hasPermission('pos', 'add') },
    { path: '/reports', icon: BarChart3, label: 'Reports', show: hasPermission('reports', 'view') },
    { path: '/reconciliation', icon: Calculator, label: 'Reconciliation', show: hasPermission('reconciliation', 'view') },
    { path: '/orders', icon: ClipboardList, label: 'Sales', show: hasPermission('sales', 'view') },
    { path: '/expenses', icon: Wallet, label: 'Expenses', show: hasPermission('expenses', 'view') },
    { path: '/menu', icon: UtensilsCrossed, label: 'Menu', show: hasPermission('menu', 'view') },
    { path: '/inventory', icon: Package, label: 'Inventory', show: hasPermission('inventory', 'view') },
    { path: '/staff', icon: UserSquare2, label: 'Staff', show: hasPermission('staff', 'view') || hasPermission('staff', 'add') || hasPermission('attendance', 'view') || hasPermission('attendance', 'add') || hasPermission('leaves', 'view') || hasPermission('leaves', 'add') || hasPermission('performance', 'view') },
    { path: '/settings', icon: Settings, label: 'Settings', show: hasPermission('settings', 'view') || hasPermission('users', 'view') || hasPermission('menu', 'view') || isAdmin() },
    { path: '/templates', icon: Layers3, label: 'Templates', show: hasPermission('settings', 'view') },
    { path: '/activity', icon: Activity, label: 'Activity Log', show: hasPermission('activity_log', 'view') },
    { path: '/ai-assistant', icon: Bot, label: 'AI Assistant', show: hasPermission('ai_assistant', 'view') },
  ];

  return (
    <div className="min-h-screen bg-gray-100 flex overflow-x-hidden">
      {/* Mobile Sidebar Overlay */}
      {mobileSidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={closeMobileSidebar}
        />
      )}

      {/* Sidebar - Desktop */}
      <aside className={`hidden lg:flex flex-col ${sidebarOpen ? 'w-64' : 'w-20'} bg-gradient-to-b from-primary-700 to-primary-900 text-white transition-all duration-300 fixed h-full z-20`}>
        <div className="p-4 flex items-center justify-between border-b border-primary-600 flex-shrink-0">
          {sidebarOpen ? (
            <div className="flex items-center gap-2">
              <img src={shopLogo} alt={shopName} className="h-10 w-auto" />
            </div>
          ) : (
            <img src={shopLogo} alt={shopName} className="h-8 w-8 object-contain" />
          )}
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-primary-600 transition"
          >
            {sidebarOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>

        <nav className="mt-4 px-2 flex-1 overflow-y-auto">
          {menuItems.filter(item => item.show).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'text-primary-100 hover:bg-white/10'
                }`
              }
            >
              <item.icon size={20} className="flex-shrink-0" />
              {sidebarOpen && <span className="truncate">{item.label}</span>}
            </NavLink>
          ))}
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-primary-600">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-primary-100 hover:bg-white/10 transition-colors"
          >
            <LogOut size={20} className="flex-shrink-0" />
            {sidebarOpen && <span className="truncate">Logout</span>}
          </button>
          {sidebarOpen && (
            <p className="mt-2 text-[10px] text-primary-300/70 text-center select-all">build {getBuildId()}</p>
          )}
        </div>
      </aside>

      {/* Sidebar - Mobile */}
      <aside className={`lg:hidden fixed h-full z-40 bg-gradient-to-b from-primary-700 to-primary-900 text-white w-64 transition-transform duration-300 flex flex-col ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-4 flex items-center justify-between border-b border-primary-600 flex-shrink-0">
          <div className="flex items-center gap-2">
            <img src={shopLogo} alt={shopName} className="h-10 w-auto" />
          </div>
          <button
            onClick={closeMobileSidebar}
            className="p-2 rounded-lg hover:bg-primary-600 transition"
          >
            <X size={20} />
          </button>
        </div>

        <nav className="mt-4 px-2 flex-1 overflow-y-auto">
          {menuItems.filter(item => item.show).map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              onClick={closeMobileSidebar}
              className={({ isActive }) =>
                `flex items-center gap-3 px-4 py-3 rounded-lg mb-1 transition-colors ${
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'text-primary-100 hover:bg-white/10'
                }`
              }
            >
              <item.icon size={20} className="flex-shrink-0" />
              <span className="truncate">{item.label}</span>
            </NavLink>
          ))}
        </nav>

        <div className="flex-shrink-0 p-4 border-t border-primary-600">
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full rounded-lg text-primary-100 hover:bg-white/10 transition-colors"
          >
            <LogOut size={20} className="flex-shrink-0" />
            <span className="truncate">Logout</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className={`flex-1 min-w-0 overflow-x-hidden transition-all duration-300 ${sidebarOpen ? 'lg:ml-64' : 'lg:ml-20'}`}>
        {/* Header */}
        <header className="bg-white shadow-sm sticky top-0 z-10">
          <div className="flex items-center justify-between px-3 sm:px-6 py-3 sm:py-4">
            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-gray-100 mr-2"
            >
              <Menu size={24} className="text-gray-600" />
            </button>

            <h2 className="text-base sm:text-xl font-semibold text-gray-800 truncate">
              Welcome, {user?.first_name || user?.username}!
            </h2>

            <div className="flex items-center gap-2 sm:gap-4">
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-2 rounded-lg hover:bg-gray-100"
                >
                  <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white font-medium">
                    {(user?.first_name?.[0] || user?.username?.[0] || 'U').toUpperCase()}
                  </div>
                  <span className="text-gray-700 hidden sm:inline">{user?.username}</span>
                  <ChevronDown size={16} className="text-gray-500 hidden sm:inline" />
                </button>

                {userMenuOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg py-2 border z-50">
                    <div className="px-4 py-2 border-b">
                      <p className="text-sm font-medium text-gray-800">{user?.first_name} {user?.last_name}</p>
                      <p className="text-xs text-gray-500 capitalize">{user?.role_name || user?.role}</p>
                    </div>
                    <button
                      onClick={handleLogout}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <LogOut size={16} />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-3 sm:p-6">
          <Outlet />
        </main>
      </div>

      {/* Offline Mode Indicator */}
      <OfflineIndicator />
    </div>
  );
};

export default Layout;
